import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Tooltip,
    Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import PaymentsExcelImport from './PaymentsExcelImport';

/**
 * Standalone dialog that hosts the PaymentsExcelImport panel:
 *   Upload → Preview → Import (+ Download Template / Export).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - clientOptions: [{ value, label }]
 *  - leadingBusiness: boolean
 *  - defaultCaseId: string | null
 *  - isImporting: boolean
 *  - onImport: (payments[]) => Promise<boolean>
 */
const PaymentsExcelImportModal = ({
    open,
    onClose,
    clientOptions = [],
    leadingBusiness = false,
    defaultCaseId = '',
    isImporting = false,
    onImport,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    // Close on successful import too (onImport returns true on success)
    const handleImport = async (rows) => {
        const ok = await onImport?.(rows);
        if (ok) {
            onClose?.();
        }
        return ok;
    };

    // Apply border-box everywhere inside the dialog so padding/borders never
    // push children out of the modal's width (fixes overflow issues in RTL).
    const borderBoxSx = {
        boxSizing: 'border-box',
        '& *, & *::before, & *::after': { boxSizing: 'border-box' },
    };

    return (
        <Dialog
            open={open}
            onClose={isImporting ? undefined : onClose}
            maxWidth="lg"
            fullWidth
            aria-labelledby="payments-excel-import-title"
            PaperProps={{
                sx: {
                    ...borderBoxSx,
                    width: '100%',
                    maxWidth: '1100px',
                    overflow: 'hidden',
                },
            }}
            sx={borderBoxSx}
        >
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, boxSizing: 'border-box' }}>
                <Tooltip title={t('Cancel')}>
                    <IconButton onClick={onClose} disabled={isImporting}>
                        <CancelIcon sx={{ color: '#304FFF' }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <DialogTitle
                id="payments-excel-import-title"
                sx={{ textAlign: 'center', pr: 5, boxSizing: 'border-box', width: '100%' }}
            >
                {t('ImportPaymentsFromExcel') || t('ImportFromExcel')}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('UploadFileThenPreviewAndImport') || ''}
                </Typography>
            </DialogTitle>

            <DialogContent
                className={direction}
                sx={{
                    pt: 1,
                    boxSizing: 'border-box',
                    width: '100%',
                    overflowX: 'hidden',
                }}
            >
                <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
                    <PaymentsExcelImport
                        clientOptions={clientOptions}
                        leadingBusiness={leadingBusiness}
                        defaultCaseId={defaultCaseId}
                        isImporting={isImporting}
                        onImport={handleImport}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'flex-end', p: 2, boxSizing: 'border-box', width: '100%' }}>
                <Button onClick={onClose} disabled={isImporting} variant="outlined" sx={{ borderRadius: '8px' }}>
                    {t('Close') || t('Cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentsExcelImportModal;
